import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  tmsApi,
  type AssignCargoItemsToLoadRequest,
  type CreateLoadRequest,
  type Load,
  type ResequenceLoadStopsRequest,
  type UpdateLoadRequest,
} from '../client';

/** Stable query-key factory for load (manifest) caches. */
export const loadKeys = {
  all: ['tms', 'load'] as const,
  list: (
    organisationId: string,
    depotSiteId: string | null,
    fromDate: string | null,
    toDate: string | null,
  ) => ['tms', 'load', 'list', organisationId, depotSiteId, fromDate, toDate] as const,
};

/** Loads for a depot/date window (lines embedded). */
export function useLoads(
  organisationId: string | null,
  params?: { depotSiteId?: string | null; fromDate?: string | null; toDate?: string | null },
) {
  const depotSiteId = params?.depotSiteId ?? null;
  const fromDate = params?.fromDate ?? null;
  const toDate = params?.toDate ?? null;
  return useQuery<Load[], Error>({
    queryKey: organisationId
      ? loadKeys.list(organisationId, depotSiteId, fromDate, toDate)
      : loadKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return tmsApi.getLoads({ organisationId, depotSiteId, fromDate, toDate });
    },
    enabled: !!organisationId,
    staleTime: 15 * 1000,
  });
}

export function useCreateLoad() {
  const qc = useQueryClient();
  return useMutation<Load, Error, CreateLoadRequest>({
    mutationFn: (body) => tmsApi.createLoad(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: loadKeys.all }),
  });
}

export function useUpdateLoad() {
  const qc = useQueryClient();
  return useMutation<Load, Error, UpdateLoadRequest>({
    mutationFn: (body) => tmsApi.updateLoad(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: loadKeys.all }),
  });
}

export function useDeleteLoad() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (id) => tmsApi.deleteLoad(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: loadKeys.all }),
  });
}

export function useAssignCargoItemsToLoad() {
  const qc = useQueryClient();
  return useMutation<Load, Error, AssignCargoItemsToLoadRequest>({
    mutationFn: (body) => tmsApi.assignCargoItemsToLoad(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: loadKeys.all }),
  });
}

/**
 * Re-sequence a load's stops.
 *
 * <p>
 * Invalidates the load caches so the manifest board and the plan queue pick up the
 * new running order. Not optimistic: the BE decides the actual line sequences from
 * the stop order, so guessing them here could show an order the server didn't
 * agree to.
 * </p>
 */
export function useResequenceLoadStops() {
  const qc = useQueryClient();
  return useMutation<Load, Error, ResequenceLoadStopsRequest>({
    mutationKey: [...loadKeys.all, 'resequenceStops'],
    mutationFn: (body) => tmsApi.resequenceLoadStops(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: loadKeys.all }),
  });
}

export function useRemoveLoadLine() {
  const qc = useQueryClient();
  return useMutation<Load, Error, string>({
    mutationFn: (lineId) => tmsApi.removeLoadLine(lineId),
    onSuccess: () => qc.invalidateQueries({ queryKey: loadKeys.all }),
  });
}
